import { toast } from "sonner";
import { rememberLinkingPhonePrefill } from "@/lib/linking-phone";
import { clearOnboardingBrowserState } from "@/lib/browser-logout-state";
import {
  ONBOARDING_DESIGN_MODE,
  getDesignModeMinWhatsappNumber,
} from "@/lib/onboarding-design-mode";
import { hasAuthenticatedStoredSupabaseSession } from "@/lib/preferences-entry-gate";
import { resolveApiBaseUrl, resolveRuntimeConfigUrl } from "@/lib/api-base-url";
import { createSingleFlightPrefetchCache } from "@/lib/link-session-prefetch";
import {
  capturePostHogException,
  getPostHogTracingHeaders,
  sanitizePostHogEvent,
  setPostHogAccount,
} from "@/lib/posthog";
import { buildUpgradeStartedPayload } from "@/lib/billing-analytics";
import { resolveFreshOwnedAccountId } from "@/lib/owned-account-selection";
import { clearSupabaseSessionStorage, getAccessToken } from "@/lib/supabase-session";
import { shouldRedirectSignedInUserToPreferences, shouldResumeLinkedAccountSetup } from "@/lib/account-linking";
import { ATTRIBUTION_KEY, captureShareLinkEntry, claimFunnelVisit } from "@/lib/share-link-capture";

const EVENT_PREFIX = "[analytics]";
const LINK_SESSION_KEY = "mincool.linkSessionId";
const ACCOUNT_ID_KEY = "mincool.accountId";
const RESPONSE_CACHE_PREFIX = "mincool.apiCache";
const RESPONSE_CACHE_MAX_AGE_MS = 30_000;
const inflightRequests = new Map();
const API_BASE_URL = resolveApiBaseUrl({
  envApiBaseUrl: import.meta.env.VITE_API_BASE_URL,
  location: typeof window !== "undefined" ? window.location : null,
});
const RUNTIME_CONFIG_URL = resolveRuntimeConfigUrl({
  envApiBaseUrl: import.meta.env.VITE_API_BASE_URL,
  location: typeof window !== "undefined" ? window.location : null,
});
const linkingSessionPrefetch = createSingleFlightPrefetchCache({ now: nowMs });
const DESIGN_MODE_CHAT_OPTIONS = [
  { id: "family-group@g.us", name: "Family Group", kind: "group", phoneNumber: null },
  { id: "ops-team@g.us", name: "Ops Team", kind: "group", phoneNumber: null },
  { id: "27820000001@c.us", name: "Tristan Drummond", kind: "direct", phoneNumber: "27820000001" },
  { id: "27820000002@c.us", name: "Sam", kind: "direct", phoneNumber: "27820000002" },
  { id: "27820000003@c.us", name: "Work", kind: "direct", phoneNumber: "27820000003" },
];

function nowMs() {
  if (typeof performance !== "undefined" && typeof performance.now === "function") {
    return performance.now();
  }
  return Date.now();
}

function emitAnalyticsEvent(eventName, properties = {}) {
  const safeProperties = sanitizePostHogEvent(properties);
  if (!safeProperties) return;
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("min-cool:analytics", {
        detail: { eventName, properties: safeProperties },
      }),
    );
  }
  console.info(EVENT_PREFIX, eventName, safeProperties);
}

function storage() {
  if (typeof window === "undefined") {
    return null;
  }
  return window.sessionStorage;
}

function storageTargets() {
  if (typeof window === "undefined") {
    return [];
  }
  return [window.sessionStorage, window.localStorage].filter(Boolean);
}

function cacheKey(name, identifier = "") {
  return `${RESPONSE_CACHE_PREFIX}.${String(name || "").trim()}${identifier ? `.${identifier}` : ""}`;
}

function readCachedResponse(key, { maxAgeMs = RESPONSE_CACHE_MAX_AGE_MS } = {}) {
  if (!key) {
    return null;
  }
  for (const store of storageTargets()) {
    const raw = store.getItem(key);
    if (!raw) {
      continue;
    }
    try {
      const payload = JSON.parse(raw);
      const cachedAt = Number(payload?.cachedAt || 0);
      if (!cachedAt || cachedAt + maxAgeMs < Date.now()) {
        store.removeItem(key);
        continue;
      }
      return payload.data ?? null;
    } catch {
      store.removeItem(key);
    }
  }
  return null;
}

function writeCachedResponse(key, data) {
  if (!key) {
    return data;
  }
  const payload = JSON.stringify({
    cachedAt: Date.now(),
    data,
  });
  for (const store of storageTargets()) {
    store.setItem(key, payload);
  }
  return data;
}

function clearCachedResponse(key) {
  if (!key) {
    return;
  }
  for (const store of storageTargets()) {
    store.removeItem(key);
  }
}

function collectChatLanguageOverrideAnalytics(data = {}) {
  const chatRules = Array.isArray(data?.chatRules) ? data.chatRules : [];
  const overrideRows = chatRules.filter((rule) => {
    if (!rule || typeof rule !== "object") {
      return false;
    }
    const targetLanguageCode = String(rule.targetLanguageCode ?? rule.target_language_code ?? "").trim();
    const deliveryLanguageMode = String(
      rule.deliveryLanguageMode ?? rule.delivery_language_mode ?? "auto",
    ).trim().toLowerCase();
    return Boolean(targetLanguageCode) || deliveryLanguageMode !== "auto";
  });
  if (!overrideRows.length) {
    return null;
  }
  return {
    override_count: overrideRows.length,
    target_language_codes: [...new Set(
      overrideRows
        .map((rule) => String(rule.targetLanguageCode ?? rule.target_language_code ?? "").trim())
        .filter(Boolean),
    )].sort(),
    delivery_language_modes: [...new Set(
      overrideRows
        .map((rule) => String(rule.deliveryLanguageMode ?? rule.delivery_language_mode ?? "auto").trim().toLowerCase())
        .filter(Boolean),
    )].sort(),
  };
}

async function withSingleFlight(key, loader) {
  if (!key) return loader();
  if (inflightRequests.has(key)) {
    return inflightRequests.get(key);
  }
  const pending = Promise.resolve()
    .then(loader)
    .finally(() => {
      inflightRequests.delete(key);
    });
  inflightRequests.set(key, pending);
  return pending;
}

function rememberAccountId(accountId) {
  if (!accountId) {
    return;
  }
  for (const store of storageTargets()) {
    store.setItem(ACCOUNT_ID_KEY, accountId);
  }
  setPostHogAccount(accountId);
}

function clearStoredKeysWithPrefix(prefix = "") {
  const normalizedPrefix = String(prefix || "").trim();
  if (!normalizedPrefix) {
    return;
  }
  for (const store of storageTargets()) {
    const keysToClear = [];
    for (let index = 0; index < store.length; index += 1) {
      const key = store.key(index);
      if (String(key || "").startsWith(normalizedPrefix)) {
        keysToClear.push(key);
      }
    }
    for (const key of keysToClear) {
      store.removeItem(key);
    }
  }
}

export function rememberSession({ linkSessionId, accountId }) {
  const store = storage();
  if (!store) return;
  if (linkSessionId) {
    store.setItem(LINK_SESSION_KEY, linkSessionId);
  }
  rememberAccountId(accountId);
  void claimFunnelVisit(accountId, { request, capturePostHogException, code: getAttributionCode() });
}


export function setCurrentAccountId(accountId) {
  const normalizedAccountId = String(accountId || "").trim();
  if (normalizedAccountId) return rememberAccountId(normalizedAccountId);
  for (const store of storageTargets()) store.removeItem(ACCOUNT_ID_KEY);
}

function apiUrl(path) {
  return `${API_BASE_URL}${path}`;
}

async function readResponseDetail(response) {
  const fallback = "Request failed";
  const payloadText = await response.text();
  if (!payloadText) {
    return fallback;
  }

  try {
    const payload = JSON.parse(payloadText);
    return payload?.detail || payload?.reason || fallback;
  } catch {
    return payloadText;
  }
}

function readServerTiming(response, metricName) {
  const raw = response.headers.get("Server-Timing") || "";
  if (!raw || !metricName) {
    return null;
  }
  const metric = raw
    .split(",")
    .map((entry) => entry.trim())
    .find((entry) => entry.startsWith(`${metricName};`));
  if (!metric) {
    return null;
  }
  const durationPart = metric
    .split(";")
    .map((entry) => entry.trim())
    .find((entry) => entry.startsWith("dur="));
  if (!durationPart) {
    return null;
  }
  const value = Number.parseFloat(durationPart.slice(4));
  return Number.isFinite(value) ? value : null;
}

function readTimingValue(payload, response, bodyKey, headerMetricName) {
  const timing = payload?.timing;
  const bodyValue = timing && typeof timing === "object" ? timing[bodyKey] : null;
  if (typeof bodyValue === "number" && Number.isFinite(bodyValue)) {
    return bodyValue;
  }
  return readServerTiming(response, headerMetricName);
}

function readResponseHeader(response, headerName) {
  return response?.headers?.get(headerName) || "";
}

async function authHeaders({ requireAuth = true, allowAnonymousBootstrap = false } = {}) {
  if (!requireAuth) {
    return {};
  }
  const token = await getAccessToken({ allowAnonymousBootstrap });
  return {
    Authorization: `Bearer ${token}`,
  };
}

export async function buildRequestHeaders(options = {}) {
  const headers = await authHeaders({
    requireAuth: options.requireAuth !== false,
    allowAnonymousBootstrap: options.allowAnonymousBootstrap === true,
  });
  return {
    "Content-Type": "application/json",
    ...getPostHogTracingHeaders(),
    ...headers,
    ...(options.headers || {}),
  };
}

export async function request(path, options = {}) {
  const { forceFresh = false, ...fetchOptions } = options;
  const response = await fetch(apiUrl(path), {
    ...(forceFresh ? { cache: "no-store" } : {}),
    ...fetchOptions,
    headers: await buildRequestHeaders(fetchOptions),
  });
  if (!response.ok) {
    throw Object.assign(new Error(await readResponseDetail(response)), { status: response.status, response });
  }
  if (response.status === 204) {
    return null;
  }
  const data = await response.json();
  if (options.includeResponseMeta) {
    return { data, response };
  }
  return data;
}

export function captureShareLink(code, options = {}) {
  return captureShareLinkEntry(code, options, { request, emitAnalyticsEvent, capturePostHogException, store: storage() });
}

export function getAttributionCode() {
  const store = storage();
  return store ? store.getItem(ATTRIBUTION_KEY) : null;
}

export function getCurrentLinkSessionId() {
  const store = storage();
  return store ? store.getItem(LINK_SESSION_KEY) : null;
}

export function getCurrentAccountId() {
  for (const store of storageTargets()) {
    const accountId = store.getItem(ACCOUNT_ID_KEY);
    if (accountId) {
      return accountId;
    }
  }
  return null;
}

async function requestLinkingSession(options = {}) {
  const relinkAccountId = String(options.relinkAccountId || "").trim();
  const testSource = String(options.testSource || "").trim();
  const path = relinkAccountId
    ? `/api/accounts/${encodeURIComponent(relinkAccountId)}/relink`
    : "/api/link-sessions";
  const body = relinkAccountId
    ? {}
    : { attributionCode: getAttributionCode() };
  if (testSource) {
    body.testSource = testSource;
  }
  const { data: payload, response } = await request(path, {
    method: "POST",
    body: JSON.stringify(body),
    includeResponseMeta: true,
    allowAnonymousBootstrap: !relinkAccountId,
  });
  rememberSession(payload);
  return { payload, response };
}

export function primeLinkingSession(options = {}) {
  const testSource = String(options.testSource || "").trim();
  return linkingSessionPrefetch.prime(() => requestLinkingSession({ testSource }));
}

export async function createLinkingSession(options = {}) {
  const { track = true, relinkAccountId = "", testSource = "" } = options;
  if (track) {
    emitAnalyticsEvent("pairing_link_started", {
      code: getAttributionCode(),
      relink: Boolean(relinkAccountId),
    });
  }
  const startedAt = nowMs();
  const consumed = relinkAccountId
    ? {
        value: await requestLinkingSession({ relinkAccountId, testSource }),
        prefetched: false,
        prefetchLeadMs: null,
      }
    : await linkingSessionPrefetch.consume(() => requestLinkingSession({ testSource }));
  const { value, prefetched, prefetchLeadMs } = consumed;
  const { payload, response } = value;
  const clientTiming = {
    prefetched,
    prefetchLeadMs,
    waitDurationMs: Math.round(nowMs() - startedAt),
  };
  if (track) {
    emitAnalyticsEvent("link_session_created", {
      code: getAttributionCode(),
      duration_ms: clientTiming.waitDurationMs,
      server_app_duration_ms: readTimingValue(payload, response, "appDurationMs", "app"),
      server_auth_duration_ms: readTimingValue(payload, response, "authDurationMs", null),
      server_db_duration_ms: readTimingValue(payload, response, "dbDurationMs", "db"),
      status: payload.status,
      reused: Boolean(payload.reusedExisting),
      prefetched,
      prefetch_lead_ms: prefetchLeadMs,
    });
  }
  return {
    ...payload,
    clientTiming,
  };
}

export async function requestPairingCode(linkSessionId, phoneNumber, options = {}) {
  const { queuedDuringBootstrap = false, waitedForSessionMs = null } = options;
  rememberLinkingPhonePrefill(phoneNumber);
  const startedAt = nowMs();
  const { data: payload, response } = await request(`/api/link-sessions/${linkSessionId}/request-code`, {
    method: "POST",
    body: JSON.stringify({ phoneNumber }),
    includeResponseMeta: true,
    allowAnonymousBootstrap: true,
  });
  emitAnalyticsEvent("pairing_code_requested", {
    code: getAttributionCode(),
    duration_ms: Math.round(nowMs() - startedAt),
    server_app_duration_ms: readTimingValue(payload, response, "appDurationMs", "app"),
    server_auth_duration_ms: readTimingValue(payload, response, "authDurationMs", null),
    server_waha_duration_ms: readTimingValue(payload, response, "wahaDurationMs", "waha"),
    queued_during_bootstrap: Boolean(queuedDuringBootstrap),
    waited_for_session_ms: waitedForSessionMs,
  });
  return payload;
}

export async function openSupportRelinkSession(token) {
  return request("/api/support/relink-sessions", {
    method: "POST",
    requireAuth: false,
    body: JSON.stringify({ token }),
  });
}

export async function openSupportEmailClaim(token) {
  return request("/api/support/email-claims/open", {
    method: "POST",
    requireAuth: false,
    body: JSON.stringify({ token }),
  });
}

export async function requestSupportRelinkCode(linkSessionId, token) {
  const normalizedLinkSessionId = String(linkSessionId || "").trim();
  if (!normalizedLinkSessionId) {
    throw new Error("Missing relink session.");
  }
  return request(`/api/support/relink-sessions/${encodeURIComponent(normalizedLinkSessionId)}/request-code`, {
    method: "POST",
    requireAuth: false,
    body: JSON.stringify({ token }),
  });
}

export async function pollSupportRelinkSession(linkSessionId, token) {
  const normalizedLinkSessionId = String(linkSessionId || "").trim();
  if (!normalizedLinkSessionId) {
    throw new Error("Missing relink session.");
  }
  return request(
    `/api/support/relink-sessions/${encodeURIComponent(normalizedLinkSessionId)}?token=${encodeURIComponent(String(token || ""))}`,
    {
      requireAuth: false,
      forceFresh: true,
    },
  );
}

export async function recordLinkClientEvent(linkSessionId, eventType, options = {}) {
  const {
    clientElapsedMs = null,
    metadata = null,
  } = options;
  const normalizedMetadata = metadata && typeof metadata === "object" ? metadata : undefined;
  await request(`/api/link-sessions/${linkSessionId}/client-event`, {
    method: "POST",
    body: JSON.stringify({
      eventType,
      clientElapsedMs,
      metadata: normalizedMetadata,
    }),
    allowAnonymousBootstrap: true,
  });
}

export async function startExistingWhatsAppOtp({ code = "", phoneNumber = "" } = {}) {
  const normalizedCode = String(code || "").trim();
  const normalizedPhoneNumber = String(phoneNumber || "").trim();
  if (!normalizedCode && !normalizedPhoneNumber) {
    throw new Error("Share code or phone number is required.");
  }
  return request("/api/onboarding/whatsapp-otp/start", {
    method: "POST",
    body: JSON.stringify({
      code: normalizedCode,
      phoneNumber: normalizedPhoneNumber,
    }),
    requireAuth: false,
  });
}

export async function getLinkingQr(linkSessionId) {
  const payload = await request(`/api/link-sessions/${linkSessionId}/qr`, {
    allowAnonymousBootstrap: true,
  });
  emitAnalyticsEvent("qr_shown", {
    code: getAttributionCode(),
  });
  return payload;
}

export async function pollLinkStatus(linkSessionId) {
  const payload = await request(`/api/link-sessions/${linkSessionId}`, {
    allowAnonymousBootstrap: true,
  });
  if (payload.account?.id) {
    rememberSession({ linkSessionId, accountId: payload.account.id });
  }
  return payload;
}

export async function saveOnboardingProfile(data) {
  const accountId = getCurrentAccountId();
  if (!accountId) {
    throw new Error("No linked account is available yet.");
  }
  const payload = await request(`/api/accounts/${accountId}/profile`, {
    method: "POST",
    body: JSON.stringify(data),
  });
  emitAnalyticsEvent("config_completed", { step: "profile" });
  return payload;
}

export async function savePreferences(data, options = {}) {
  const accountId = String(options.accountId || getCurrentAccountId() || "").trim();
  if (!accountId) {
    throw new Error("No linked account is available yet.");
  }
  const payload = await request(`/api/accounts/${accountId}/preferences`, {
    method: "POST",
    body: JSON.stringify(data),
  });
  const existingAccountCache = readCachedMyAccount(accountId);
  if (existingAccountCache) {
    primeMyAccountCache(accountId, {
      ...existingAccountCache,
      preferences: payload.preferences,
    });
  }
  const existingBootstrapCache = readCachedPreferencesBootstrap(accountId);
  if (existingBootstrapCache) {
    primePreferencesBootstrapCache(accountId, {
      ...existingBootstrapCache,
      preferences: payload.preferences,
    });
  }
  const chatLanguageOverrideAnalytics = collectChatLanguageOverrideAnalytics(data);
  if (chatLanguageOverrideAnalytics) {
    emitAnalyticsEvent("chat_language_override_saved", chatLanguageOverrideAnalytics);
  }
  emitAnalyticsEvent("config_completed", { step: "preferences" });
  return payload;
}

export async function saveUiLanguage(languageCode) {
  const payload = await request("/api/me/ui-language", {
    method: "POST",
    body: JSON.stringify({ languageCode }),
  });
  const currentAccountId = getCurrentAccountId();
  const existingAccountsCache = readCachedMyAccounts();
  if (existingAccountsCache) {
    primeMyAccountsCache({
      ...existingAccountsCache,
      preferredUiLanguageCode: payload?.preferredUiLanguageCode || languageCode,
    });
  }
  if (currentAccountId) {
    const existingAccountCache = readCachedMyAccount(currentAccountId);
    if (existingAccountCache?.account) {
      primeMyAccountCache(currentAccountId, {
        ...existingAccountCache,
        account: {
          ...existingAccountCache.account,
          preferredUiLanguageCode: payload?.preferredUiLanguageCode || languageCode,
        },
      });
    }
    const existingBootstrapCache = readCachedPreferencesBootstrap(currentAccountId);
    if (existingBootstrapCache?.account) {
      primePreferencesBootstrapCache(currentAccountId, {
        ...existingBootstrapCache,
        account: {
          ...existingBootstrapCache.account,
          preferredUiLanguageCode: payload?.preferredUiLanguageCode || languageCode,
        },
      });
    }
  }
  emitAnalyticsEvent("ui_language_selected", {
    language_code: String(payload?.preferredUiLanguageCode || languageCode || "").trim(),
  });
  return payload;
}

export async function saveAccountLanguages(accountId, data = {}) {
  const normalizedAccountId = String(accountId || getCurrentAccountId() || "").trim();
  if (!normalizedAccountId) {
    throw new Error("No linked account is available yet.");
  }
  const payload = await request(`/api/accounts/${normalizedAccountId}/languages`, {
    method: "POST",
    body: JSON.stringify(data),
  });
  const existingAccountCache = readCachedMyAccount(normalizedAccountId);
  if (existingAccountCache?.profile) {
    primeMyAccountCache(normalizedAccountId, {
      ...existingAccountCache,
      account: {
        ...existingAccountCache.account,
        preferredUiLanguageCode: payload?.preferredUiLanguageCode || existingAccountCache.account?.preferredUiLanguageCode,
      },
      profile: {
        ...existingAccountCache.profile,
        preferredUiLanguageCode: payload?.preferredUiLanguageCode || existingAccountCache.profile?.preferredUiLanguageCode,
        languagePreferences: payload?.languagePreferences || existingAccountCache.profile?.languagePreferences,
      },
    });
  }
  const existingBootstrapCache = readCachedPreferencesBootstrap(normalizedAccountId);
  if (existingBootstrapCache?.profile) {
    primePreferencesBootstrapCache(normalizedAccountId, {
      ...existingBootstrapCache,
      account: {
        ...existingBootstrapCache.account,
        preferredUiLanguageCode: payload?.preferredUiLanguageCode || existingBootstrapCache.account?.preferredUiLanguageCode,
      },
      profile: {
        ...existingBootstrapCache.profile,
        preferredUiLanguageCode: payload?.preferredUiLanguageCode || existingBootstrapCache.profile?.preferredUiLanguageCode,
        languagePreferences: payload?.languagePreferences || existingBootstrapCache.profile?.languagePreferences,
      },
    });
  }
  const existingAccountsCache = readCachedMyAccounts();
  if (existingAccountsCache) {
    const primaryAccountId = String(existingAccountsCache.primaryAccountId || "").trim();
    const isPrimaryAccount = primaryAccountId && primaryAccountId === normalizedAccountId;
    primeMyAccountsCache({
      ...existingAccountsCache,
      preferredUiLanguageCode: payload?.preferredUiLanguageCode || existingAccountsCache.preferredUiLanguageCode,
      profile: isPrimaryAccount
        ? {
            ...(existingAccountsCache.profile || {}),
            preferredUiLanguageCode: payload?.preferredUiLanguageCode || existingAccountsCache.profile?.preferredUiLanguageCode,
            languagePreferences: payload?.languagePreferences || existingAccountsCache.profile?.languagePreferences,
          }
        : existingAccountsCache.profile,
    });
  }
  emitAnalyticsEvent("language_profile_saved", {
    account_id: normalizedAccountId,
    home_language_code: payload?.languagePreferences?.homeLanguageCode || "",
  });
  return payload;
}

export function readCachedMyAccount(accountId = "") {
  return readCachedResponse(cacheKey("me.account", String(accountId || "").trim() || "default"));
}

export function primeMyAccountCache(accountId = "", payload = null) {
  return writeCachedResponse(
    cacheKey("me.account", String(accountId || "").trim() || "default"),
    payload,
  );
}

export function readCachedPreferencesBootstrap(accountId = "") {
  return readCachedResponse(
    cacheKey("me.preferences-bootstrap", String(accountId || "").trim() || "default"),
  );
}

export function primePreferencesBootstrapCache(accountId = "", payload = null) {
  return writeCachedResponse(
    cacheKey("me.preferences-bootstrap", String(accountId || "").trim() || "default"),
    payload,
  );
}

export async function getMyAccount(accountId = "", options = {}) {
  const normalizedAccountId = String(accountId || "").trim();
  if (!options.forceFresh) {
    const cachedPayload = readCachedMyAccount(normalizedAccountId);
    if (cachedPayload) {
      return cachedPayload;
    }
  }
  const load = async () => {
    const path = normalizedAccountId
      ? `/api/me/account?accountId=${encodeURIComponent(normalizedAccountId)}`
      : "/api/me/account";
    const payload = await request(path);
    if (payload?.account?.id && hasAuthenticatedStoredSupabaseSession()) {
      rememberSession({ accountId: payload.account.id });
      primeMyAccountCache(payload.account.id, payload);
      primePreferencesBootstrapCache(payload.account.id, payload);
    }
    primeMyAccountCache(normalizedAccountId, payload);
    primePreferencesBootstrapCache(normalizedAccountId, payload);
    return payload;
  };
  return options.forceFresh ? load() : withSingleFlight(`me.account:${normalizedAccountId || "default"}`, load);
}

export async function getPreferencesBootstrap(accountId = "", options = {}) {
  const normalizedAccountId = String(accountId || "").trim();
  if (!options.forceFresh) {
    const cachedPayload = readCachedPreferencesBootstrap(normalizedAccountId);
    if (cachedPayload) {
      return cachedPayload;
    }
  }
  const load = async () => {
    const path = normalizedAccountId
      ? `/api/me/preferences-bootstrap?accountId=${encodeURIComponent(normalizedAccountId)}`
      : "/api/me/preferences-bootstrap";
    const payload = await request(path);
    if (payload?.account?.id && hasAuthenticatedStoredSupabaseSession()) {
      rememberSession({ accountId: payload.account.id });
      primePreferencesBootstrapCache(payload.account.id, payload);
    }
    primePreferencesBootstrapCache(normalizedAccountId, payload);
    return payload;
  };
  return options.forceFresh ? load() : withSingleFlight(`me.preferences-bootstrap:${normalizedAccountId || "default"}`, load);
}

export async function getPreferencesBootstrapDecision(accountId = "", options = {}) {
  try {
    const accountPayload = await getPreferencesBootstrap(accountId, options);
    const resolvedAccountId = String(accountPayload?.account?.id || accountPayload?.primaryAccountId || "").trim();
    const accountStatus = String(accountPayload?.account?.status || "").trim() || null;
    return {
      destination: shouldRedirectSignedInUserToPreferences(accountPayload) ? "preferences" : shouldResumeLinkedAccountSetup(accountPayload) ? "setup" : "link",
      accountId: resolvedAccountId || null,
      accountStatus,
      accountPayload,
      timing: accountPayload?.timing || null,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : "";
    if (/no linked account found/i.test(message)) {
      return {
        destination: "link",
        accountId: null,
        accountStatus: null,
        accountPayload: null,
        timing: null,
      };
    }
    throw error;
  }
}

function myAccountsCacheVariant(options = {}) {
  return options.includeSubscriptionSummary ? "with-summary" : "default";
}

function myAccountsCacheKey(options = {}) {
  return cacheKey("me.accounts", myAccountsCacheVariant(options));
}

export function readCachedMyAccounts(options = {}) {
  const cachedPayload = readCachedResponse(myAccountsCacheKey(options));
  if (cachedPayload) {
    return cachedPayload;
  }
  if (options.includeSubscriptionSummary) {
    return null;
  }
  return readCachedResponse(cacheKey("me.accounts"));
}

export function primeMyAccountsCache(payload = null, options = {}) {
  return writeCachedResponse(myAccountsCacheKey(options), payload);
}
export async function listMyAccounts(options = {}) {
  if (!options.forceFresh) {
    const cachedPayload = readCachedMyAccounts(options);
    if (cachedPayload) {
      return cachedPayload;
    }
  }
  const load = async () => {
    const params = new URLSearchParams();
    if (options.includeSubscriptionSummary) {
      params.set("includeSubscriptionSummary", "1");
    }
    const path = params.size ? `/api/me/accounts?${params.toString()}` : "/api/me/accounts";
    const payload = await request(path);
    const selectedAccountId = getCurrentAccountId();
    const nextAccountId = resolveFreshOwnedAccountId(payload, selectedAccountId);
    if (hasAuthenticatedStoredSupabaseSession()) {
      if (nextAccountId) rememberSession({ accountId: nextAccountId });
      else if (selectedAccountId) setCurrentAccountId("");
    }
    primeMyAccountsCache(payload, options);
    return payload;
  };
  return options.forceFresh ? load() : withSingleFlight(`me.accounts:${myAccountsCacheVariant(options)}`, load);
}

export async function setMyPrimaryAccount(accountId) {
  const normalizedAccountId = String(accountId || "").trim();
  if (!normalizedAccountId) {
    throw new Error("No linked account is available yet.");
  }
  const payload = await request("/api/me/accounts/primary", {
    method: "POST",
    body: JSON.stringify({ accountId: normalizedAccountId }),
  });
  rememberSession({ accountId: normalizedAccountId });
  clearStoredKeysWithPrefix(cacheKey("me.accounts"));
  clearCachedResponse(cacheKey("me.account", "default"));
  clearCachedResponse(cacheKey("me.preferences-bootstrap", "default"));
  return payload;
}

export async function completeAccountOnboarding(accountId) {
  const normalizedAccountId = String(accountId || getCurrentAccountId() || "").trim();
  if (!normalizedAccountId) {
    throw new Error("No linked account is available yet.");
  }
  const payload = await request(`/api/accounts/${normalizedAccountId}/onboarding-complete`, {
    method: "POST",
  });
  if (payload?.account) {
    const existingAccountCache = readCachedMyAccount(normalizedAccountId);
    const existingBootstrapCache = readCachedPreferencesBootstrap(normalizedAccountId);
    const nextAccountPayload = {
      ...(existingAccountCache || {}),
      account: payload.account,
      preferences: payload.preferences || existingAccountCache?.preferences || {},
    };
    primeMyAccountCache(normalizedAccountId, nextAccountPayload);
    primeMyAccountCache("", nextAccountPayload);
    primePreferencesBootstrapCache(normalizedAccountId, {
      ...(existingBootstrapCache || nextAccountPayload),
      account: payload.account,
      preferences: payload.preferences || existingBootstrapCache?.preferences || nextAccountPayload.preferences || {},
    });
    primePreferencesBootstrapCache("", { ...nextAccountPayload, account: payload.account });
  }
  return payload;
}

export async function setAccountOnboardingPlanSelection(accountId, plan, options = {}) {
  const normalizedAccountId = String(accountId || getCurrentAccountId() || "").trim();
  if (!normalizedAccountId) {
    throw new Error("No linked account is available yet.");
  }
  const normalizedPlan = String(plan || "").trim().toLowerCase();
  const payload = await request(`/api/accounts/${normalizedAccountId}/onboarding-plan-selection`, {
    method: "POST",
    body: JSON.stringify({ plan: normalizedPlan }),
  });
  emitAnalyticsEvent("onboarding_plan_selected", {
    selected_plan: normalizedPlan,
    source: String(options.source || "setup_pricing").trim() || "setup_pricing",
    offer_type: String(options.offerType || "standard").trim() || "standard",
  });
  return payload;
}

export async function getReferralsOverview(accountId) {
  const normalizedAccountId = String(accountId || getCurrentAccountId() || "").trim();
  const query = normalizedAccountId ? `?accountId=${encodeURIComponent(normalizedAccountId)}` : "";
  return request(`/api/referrals/overview${query}`);
}

export async function listAccountChats(accountId, options = {}) {
  const normalizedAccountId = String(accountId || "").trim();
  const limit = Number.isFinite(options.limit) ? options.limit : 100;
  const query = String(options.query || "").trim();
  const selectedOnly = options.selectedOnly === true;
  const includeIds = Array.isArray(options.includeIds)
    ? [...new Set(options.includeIds.map((item) => String(item || "").trim()).filter(Boolean))]
    : [];
  const selectedDesignChats = DESIGN_MODE_CHAT_OPTIONS.filter((chat) => includeIds.includes(chat.id));
  const filteredDesignChats = query
    ? DESIGN_MODE_CHAT_OPTIONS.filter((chat) => {
        const haystack = [chat.name, chat.id, chat.phoneNumber, chat.kind].join(" ").toLowerCase();
        return haystack.includes(query.toLowerCase());
      })
    : DESIGN_MODE_CHAT_OPTIONS;
  const defaultPayload = {
    chats: selectedOnly ? selectedDesignChats : filteredDesignChats,
    meta: {
      visibleRecentCount: selectedOnly ? 0 : Math.min(limit, filteredDesignChats.length),
      selectedCount: selectedDesignChats.length,
      cachedCount: DESIGN_MODE_CHAT_OPTIONS.length,
      syncing: false,
      query,
    },
  };

  if (!normalizedAccountId) {
    return defaultPayload;
  }

  const params = new URLSearchParams({ limit: String(limit) });
  if (query) {
    params.set("query", query);
  }
  if (selectedOnly) {
    params.set("selectedOnly", "true");
  }
  for (const chatId of includeIds) {
    params.append("includeIds", chatId);
  }
  const payload = await request(`/api/accounts/${normalizedAccountId}/chats?${params.toString()}`);
  return {
    chats: Array.isArray(payload?.chats) ? payload.chats : [],
    meta: payload?.meta && typeof payload.meta === "object"
      ? payload.meta
      : {
          visibleRecentCount: 0,
          selectedCount: includeIds.length,
          cachedCount: 0,
          syncing: false,
          query,
        },
  };
}

export function createCheckoutSession(plan, options = {}) {
  const { source = "unknown" } = options;
  const accountId = options.accountId || getCurrentAccountId();
  emitAnalyticsEvent("upgrade_started", {
    from_plan: options.fromPlan || "free",
    to_plan: plan,
    source,
  });
  const params = new URLSearchParams({ plan, source });
  if (accountId) {
    params.set("accountId", accountId);
  }
  const url = `/billing?${params.toString()}`;
  if (typeof window !== "undefined") {
    window.location.assign(url);
  }
  return Promise.resolve({ url, plan, accountId });
}

export async function createDemoShareLink() {
  return request("/api/dev/demo-share-links", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function getLocalDevBootstrap() {
  return request("/api/dev/local-bootstrap", {
    method: "POST",
    requireAuth: false,
  });
}

export async function createOnboardingHandoff(accountId) {
  return request("/api/onboarding/handoffs", {
    method: "POST",
    body: JSON.stringify({ accountId }),
  });
}

export async function getOnboardingEmailStatus(email) {
  return request("/api/onboarding/email-status", {
    method: "POST",
    body: JSON.stringify({ email }),
    requireAuth: false,
  });
}

export async function captureOnboardingEmail(data) {
  return request("/api/onboarding/email-capture", {
    method: "POST",
    body: JSON.stringify({
      accountId: getCurrentAccountId(),
      ...data,
    }),
  });
}

export async function adoptOnboardingAccount(token, extra = {}) {
  return request("/api/onboarding/adopt-account", {
    method: "POST",
    body: JSON.stringify({ token, ...extra }),
  });
}

export async function finalizeOnboardingSignup(data) {
  const accountId = data?.accountId || getCurrentAccountId();
  if (!accountId) {
    throw new Error("No linked account is available yet.");
  }
  return request("/api/onboarding/finalize-signup", {
    method: "POST",
    body: JSON.stringify({
      accountId,
      ...data,
    }),
  });
}

export async function setAccountPlan(plan, options = {}) {
  const accountId = getCurrentAccountId();
  if (!accountId) {
    throw new Error("No linked account is available yet.");
  }
  return request(`/api/dev/accounts/${accountId}/plan`, {
    method: "POST",
    body: JSON.stringify({ plan, cadence: options.cadence || "" }),
  });
}

export function clearOnboardingClientState() {
  clearOnboardingBrowserState();
  clearSupabaseSessionStorage();
}

let runtimeConfigPromise = null;

export async function getRuntimeConfig() {
  if (ONBOARDING_DESIGN_MODE) {
    return {
      minWhatsappNumber: getDesignModeMinWhatsappNumber(),
      countryCode: "ZA",
      countryLanguageSuggestions: ["en", "af", "zu", "xh"],
      languageCatalog: [],
      defaultLanguageCode: "en",
    };
  }
  if (!runtimeConfigPromise) {
    runtimeConfigPromise = fetch(RUNTIME_CONFIG_URL, {
      headers: await buildRequestHeaders({ requireAuth: false }),
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(await readResponseDetail(response));
        }
        return response.json();
      })
      .catch((error) => {
        runtimeConfigPromise = null;
        throw error;
      });
  }
  return runtimeConfigPromise;
}

function normalizeWhatsappNumber(value) {
  return String(value || "").replace(/\D+/g, "");
}

export async function openWhatsAppChat(input = "unknown") {
  const options = typeof input === "string" ? { sourceStep: input } : (input || {});
  const sourceStep = String(options.sourceStep || "unknown").trim() || "unknown";
  emitAnalyticsEvent("whatsapp_dm_opened", {
    source_step: sourceStep,
  });
  try {
    const config = await getRuntimeConfig();
    const targetNumber = normalizeWhatsappNumber(options.phoneNumber) || config.minWhatsappNumber;
    const prefilledText = String(options.prefilledText || "").trim();
    const query = prefilledText ? `?text=${encodeURIComponent(prefilledText)}` : "";
    const url = `https://wa.me/${targetNumber}${query}`;
    window.open(url, "_blank", "noopener,noreferrer");
  } catch (error) {
    capturePostHogException(error, {
      error_context: "open_whatsapp_chat_failed",
      source_step: sourceStep,
    });
    toast.error("Could not open WhatsApp", {
      description: error.message,
    });
  }
}

export async function openMinSupportChat(input = "unknown") {
  const options = typeof input === "string" ? { sourceStep: input } : (input || {});
  const prefilledText = typeof options.prefilledText === "string"
    ? options.prefilledText
    : "Hi Min support, I need help.";
  return openWhatsAppChat({
    sourceStep: String(options.sourceStep || "min_support").trim() || "min_support",
    prefilledText,
  });
}

export function trackEvent(name, payload = {}) {
  emitAnalyticsEvent(name, payload);
}

export function __clearApiClientTestState() {
  inflightRequests.clear();
}

export function readCachedBillingSubscription(accountId = "") {
  return readCachedResponse(cacheKey("billing.subscription", String(accountId || "").trim()));
}

export function primeBillingSubscriptionCache(accountId = "", payload = null) {
  return writeCachedResponse(cacheKey("billing.subscription", String(accountId || "").trim()), payload);
}

function toBillingSummaryPayload(payload = null) {
  if (!payload || typeof payload !== "object") {
    return null;
  }
  return {
    accountId: payload.accountId || null,
    billingAccountId: payload.billingAccountId || null,
    currentPlan: payload.currentPlan || "free",
    subscription: payload.subscription || null,
    billingOwnerDisplayName: payload.billingOwnerDisplayName || null,
    billingOwnerPhoneNumber: payload.billingOwnerPhoneNumber || null,
    timing: payload.timing || null,
  };
}

export function readCachedBillingSummary(accountId = "") {
  const normalizedAccountId = String(accountId || "").trim();
  const cachedSummary = readCachedResponse(cacheKey("billing.summary", normalizedAccountId));
  if (cachedSummary) {
    return cachedSummary;
  }
  return toBillingSummaryPayload(readCachedBillingSubscription(normalizedAccountId));
}

export function primeBillingSummaryCache(accountId = "", payload = null) {
  return writeCachedResponse(
    cacheKey("billing.summary", String(accountId || "").trim()),
    toBillingSummaryPayload(payload),
  );
}

function primeRelatedBillingSummaryCaches(accountId = "", payload = null) {
  const normalizedAccountId = String(accountId || "").trim();
  const normalizedBillingAccountId = String(payload?.billingAccountId || "").trim();
  const summaryPayload = toBillingSummaryPayload(payload);
  const cacheIds = [...new Set([normalizedAccountId, normalizedBillingAccountId].filter(Boolean))];
  for (const cacheId of cacheIds) {
    primeBillingSummaryCache(cacheId, summaryPayload);
  }
  return summaryPayload;
}

export async function preloadExistingPreferencesRoute(accountId = "", options = {}) {
  const normalizedAccountId = String(accountId || "").trim();
  const forceFresh = options.forceFresh === true;
  const accountStartedAt = nowMs();
  const accountPayload = options.accountPayload || await getPreferencesBootstrap(normalizedAccountId, { forceFresh });
  const accountFetchDurationMs = Math.round(nowMs() - accountStartedAt);
  const resolvedAccountId = normalizedAccountId
    || String(accountPayload?.account?.id || "").trim()
    || String(accountPayload?.primaryAccountId || "").trim();
  return {
    accountId: resolvedAccountId || null,
    accountPayload,
    accountsPayload: null,
    billingPayload: null,
    accountFetchDurationMs,
    billingFetchDurationMs: null,
  };
}

export async function getBillingSubscription(accountId, options = {}) {
  if (!accountId) {
    throw new Error("No linked account is available yet.");
  }
  const normalizedAccountId = String(accountId || "").trim();
  const cachedPayload = !options.forceFresh
    ? readCachedBillingSubscription(normalizedAccountId)
    : null;
  if (cachedPayload) {
    return cachedPayload;
  }
  return withSingleFlight(`billing.subscription:${normalizedAccountId}`, async () => {
    const payload = await request(`/api/billing/subscription?accountId=${encodeURIComponent(accountId)}`);
    primeBillingSubscriptionCache(normalizedAccountId, payload);
    primeRelatedBillingSummaryCaches(normalizedAccountId, payload);
    return payload;
  });
}

export async function getBillingSummary(accountId, options = {}) {
  if (!accountId) {
    throw new Error("No linked account is available yet.");
  }
  const normalizedAccountId = String(accountId || "").trim();
  const cachedPayload = !options.forceFresh
    ? readCachedBillingSummary(normalizedAccountId)
    : null;
  if (cachedPayload) {
    return cachedPayload;
  }
  return withSingleFlight(`billing.summary:${normalizedAccountId}`, async () => {
    const payload = await request(`/api/billing/summary?accountId=${encodeURIComponent(accountId)}`);
    primeRelatedBillingSummaryCaches(normalizedAccountId, payload);
    return payload;
  });
}

export async function startBillingCheckout({
  accountId,
  priceId,
  toPlan = "",
  source = "billing_page",
  returnContext = "billing",
  onboardingMode = "",
  fromPlan = "free",
  billingCycle = "",
  offerType = "",
  offerId = "",
  returnTo = "",
  checkoutStartedAtMs = null,
}) {
  if (!accountId) {
    throw new Error("No linked account is available yet.");
  }
  if (!priceId) {
    throw new Error("No billing price is available yet.");
  }
  const analyticsPayload = buildUpgradeStartedPayload({
    fromPlan,
    toPlan,
    source,
    priceId,
    billingCycle,
    offerType,
  });
  const requestStartedAtMs = nowMs();
  emitAnalyticsEvent("upgrade_started", analyticsPayload);
  const { data, response } = await request("/api/billing/subscriptions/create", {
    method: "POST",
    body: JSON.stringify({ accountId, priceId, source, returnContext, onboardingMode, offerId, returnTo: returnTo === "/agent" ? returnTo : "" }),
    includeResponseMeta: true,
  });
  const responseReceivedAtMs = nowMs();
  const normalizedCheckoutStartedAtMs = Number(checkoutStartedAtMs);
  const clientDurationMs = Number.isFinite(normalizedCheckoutStartedAtMs)
    ? Math.max(0, Math.round(responseReceivedAtMs - normalizedCheckoutStartedAtMs))
    : null;
  emitAnalyticsEvent("checkout_handoff_ready", {
    ...analyticsPayload,
    app_duration_ms: readTimingValue(data, response, "appDurationMs", "app"),
    auth_duration_ms: readTimingValue(data, response, "authDurationMs", "auth"),
    db_duration_ms: readTimingValue(data, response, "dbDurationMs", "db"),
    identity_duration_ms: readTimingValue(data, response, "identityDurationMs", "identity"),
    client_duration_ms: clientDurationMs,
    request_duration_ms: Math.max(0, Math.round(responseReceivedAtMs - requestStartedAtMs)),
    request_id: readResponseHeader(response, "X-Request-Id"),
    response_status: response.status,
    route_duration_ms: Number.parseInt(readResponseHeader(response, "X-Route-Duration-Ms"), 10) || null,
  });
  return data;
}

export async function resolvePaymentReturnTarget(token) {
  const normalizedToken = String(token || "").trim();
  if (!normalizedToken) {
    return {
      payment: "invalid_token",
      transactionId: null,
      accountId: null,
      returnContext: "billing",
      onboardingMode: "existing",
      selectedPlan: "",
      redirectUrl: "/billing?payment=invalid_token",
    };
  }
  return request(`/api/payments/return-target?token=${encodeURIComponent(normalizedToken)}`, {
    requireAuth: false,
    forceFresh: true,
  });
}

export async function getOnboardingPromoOffer(accountId) {
  if (!accountId) {
    throw new Error("No linked account is available yet.");
  }
  return request(`/api/onboarding/promo-offer?accountId=${encodeURIComponent(accountId)}`);
}

export async function setOnboardingPromoDecision({ accountId, decision, selectedPriceId = "" }) {
  if (!accountId) {
    throw new Error("No linked account is available yet.");
  }
  return request("/api/onboarding/promo-offer/decision", {
    method: "POST",
    body: JSON.stringify({ accountId, decision, selectedPriceId }),
  });
}

export async function cancelBillingSubscription(accountId) {
  if (!accountId) {
    throw new Error("No linked account is available yet.");
  }
  const payload = await request("/api/billing/subscriptions/cancel", {
    method: "POST",
    body: JSON.stringify({ accountId }),
  });
  primeBillingSubscriptionCache(accountId, payload);
  primeRelatedBillingSummaryCaches(accountId, payload);
  return payload;
}

export async function downgradeBillingSubscription(accountId, plan) {
  if (!accountId) {
    throw new Error("No linked account is available yet.");
  }
  const payload = await request("/api/billing/subscriptions/downgrade", {
    method: "POST",
    body: JSON.stringify({ accountId, plan }),
  });
  primeBillingSubscriptionCache(accountId, payload);
  primeRelatedBillingSummaryCaches(accountId, payload);
  return payload;
}

export async function clearScheduledDowngrade(accountId) {
  if (!accountId) {
    throw new Error("No linked account is available yet.");
  }
  const payload = await request(`/api/billing/subscriptions/downgrade?accountId=${encodeURIComponent(accountId)}`, {
    method: "DELETE",
  });
  primeBillingSubscriptionCache(accountId, payload);
  primeRelatedBillingSummaryCaches(accountId, payload);
  return payload;
}

export function submitPayFastPaymentForm({ redirectUrl, paymentData }) {
  if (typeof document === "undefined") {
    throw new Error("PayFast checkout requires a browser.");
  }
  const form = document.createElement("form");
  form.method = "POST";
  form.action = redirectUrl;
  Object.entries(paymentData || {}).forEach(([key, value]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = key;
    input.value = value == null ? "" : String(value);
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
}
